import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import Icon from '../icon/Icon'
import Text from '../Text'
import Popover from '../Popover'
import { NewDot } from '../SecondaryNavigation'

import { useAppState } from '../../context/AppContext'

import { ICompanyTerm } from '../../common/models/Company'

interface IProps {
  selectedTerm?: ICompanyTerm
  compareTo?: ICompanyTerm
  termOptions: ICompanyTerm[]
  allowCompare: boolean
  onSelectTerm: (term: ICompanyTerm) => void
  onSetCompareTo: (term: ICompanyTerm | undefined) => void
  // Terms that hold something waiting on the viewer get a red dot in the list,
  // and the chip gets one too, so the closed filter already says there is one
  hasDot?: (term: ICompanyTerm) => boolean
}

// Chip + popover for choosing which evaluation term the insights view shows.
// The popover lists all terms (primary selection) and, when allowCompare is
// set, a toggle that reveals a second list to pick a term to compare against.
// Selecting the active compare term as primary clears the comparison. Term
// selection and compare state are lifted to the parent via the callbacks.
const TermFilter: React.FC<IProps> = ({
  selectedTerm,
  compareTo,
  termOptions,
  allowCompare,
  onSelectTerm,
  onSetCompareTo,
  hasDot,
}) => {
  const { t } = useTranslation()
  const { theme } = useAppState()
  const chipRef = useRef<HTMLButtonElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [compareEnabled, setCompareEnabled] = useState<boolean>(!!compareTo)

  if (!selectedTerm || termOptions.length === 0) return null

  const sortedTerms = [...termOptions].sort(
    (a, b) => b.termNumber - a.termNumber
  )
  const compareOptions = sortedTerms.filter(
    (term) => term.termNumber !== selectedTerm.termNumber
  )
  const hasAnyDot = !!hasDot && termOptions.some((term) => hasDot(term))

  // When dropdown opens, sync compareEnabled with current compareTo state
  const handleToggleOpen = () => {
    setIsOpen((prev) => {
      const next = !prev
      if (next) setCompareEnabled(!!compareTo)
      return next
    })
  }

  const handleClose = () => setIsOpen(false)

  const handlePickPrimary = (term: ICompanyTerm) => {
    onSelectTerm(term)
    if (compareTo && term.termNumber === compareTo.termNumber) {
      onSetCompareTo(undefined)
    }
    handleClose()
  }

  const handlePickCompare = (term: ICompanyTerm) => {
    onSetCompareTo(term)
    handleClose()
  }

  const handleToggleCompare = () => {
    if (compareEnabled) {
      setCompareEnabled(false)
      if (compareTo) onSetCompareTo(undefined)
    } else {
      if (compareOptions.length === 0) return
      setCompareEnabled(true)
    }
  }

  return (
    <Wrapper>
      <PrimaryChip
        ref={chipRef}
        type="button"
        onClick={handleToggleOpen}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        $open={isOpen}
      >
        <Icon type="Calendar" size={13} color={theme.colors.primary} />
        <Text textStyle="labelHeavy" color="textDark" tag="span">
          {selectedTerm.name}
        </Text>
        {hasAnyDot && <NewDot />}
        <Caret $open={isOpen}>
          <Icon type="ChevronDown" size={10} color={theme.colors.text72} />
        </Caret>
      </PrimaryChip>

      <Popover
        anchor={chipRef.current}
        isOpen={isOpen}
        onClose={handleClose}
        placement="bottom-start"
        offset={8}
        ariaLabel={t('userProfileTermReviews.selectTerm')}
      >
        <DropdownContent>
          <Section>
            <SectionLabel
              textStyle="labelSmallCaps"
              color="textLight"
              tag="span"
            >
              {t('trends.term')}
            </SectionLabel>
            <OptionList role="listbox">
              {sortedTerms.map((term) => {
                const active = term.termNumber === selectedTerm.termNumber
                return (
                  <OptionRow
                    key={`primary-${term.termNumber}`}
                    type="button"
                    role="option"
                    aria-selected={active}
                    $active={active}
                    onClick={() => handlePickPrimary(term)}
                  >
                    <Radio $active={active} />
                    <Text textStyle="label" color="textDark" tag="span">
                      {term.name}
                    </Text>
                    {hasDot?.(term) && <OptionDot />}
                  </OptionRow>
                )
              })}
            </OptionList>
          </Section>

          {allowCompare && (
            <>
              <Divider />
              <Section>
                <ToggleRow
                  type="button"
                  onClick={handleToggleCompare}
                  disabled={compareOptions.length === 0}
                  aria-pressed={compareEnabled}
                >
                  <Text textStyle="labelSmallCaps" color="textLight" tag="span">
                    {t('general.actions.compare')}
                  </Text>
                  <SwitchTrack $on={compareEnabled}>
                    <SwitchThumb $on={compareEnabled} />
                  </SwitchTrack>
                </ToggleRow>
                {compareOptions.length === 0 && (
                  <DisabledHint
                    textStyle="caption"
                    color="textLight"
                    tag="span"
                  >
                    {t('trends.compareDisabledNoOther')}
                  </DisabledHint>
                )}
                {compareEnabled && compareOptions.length > 0 && (
                  <CompareList role="listbox">
                    {compareOptions.map((term) => {
                      const active = term.termNumber === compareTo?.termNumber
                      return (
                        <OptionRow
                          key={`compare-${term.termNumber}`}
                          type="button"
                          role="option"
                          aria-selected={active}
                          $active={active}
                          onClick={() => handlePickCompare(term)}
                        >
                          <Radio $active={active} />
                          <Text textStyle="label" color="textDark" tag="span">
                            {term.name}
                          </Text>
                        </OptionRow>
                      )
                    })}
                  </CompareList>
                )}
              </Section>
            </>
          )}
        </DropdownContent>
      </Popover>
    </Wrapper>
  )
}

export default TermFilter

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
`

const PrimaryChip = styled.button<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  height: 32px;
  box-sizing: border-box;
  cursor: pointer;
  border: none;
  background: ${({ theme, $open }) =>
    $open ? theme.colors.primaryFadeLight : theme.colors.primaryLight};
  transition: background-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryFadeLight};
  }
`

const Caret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
  transition: transform 0.18s ease;

  > span {
    margin-right: 0;
  }
`

const DropdownContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 0;
  min-width: 260px;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const SectionLabel = styled(Text)`
  padding: 0 16px 4px;
`

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 240px;
  overflow-y: auto;
`

const CompareList = styled(OptionList)`
  margin-top: 4px;
`

const OptionRow = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  padding: 8px 16px;
  border: none;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primaryFadeLight : 'transparent'};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryFadeLight};
  }
`

const Radio = styled.span<{ $active: boolean }>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.primary : theme.colors.textLighter};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primary : 'transparent'};
  box-shadow: ${({ theme, $active }) =>
    $active ? `inset 0 0 0 2px ${theme.colors.lightest}` : 'none'};
  flex-shrink: 0;
  transition: background-color 0.15s ease, border-color 0.15s ease;
`

const OptionDot = styled(NewDot)`
  margin-left: auto;
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.textLighter};
  margin: 8px 0;
`

const ToggleRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  width: 100%;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryFadeLight};
  }
`

const SwitchTrack = styled.span<{ $on: boolean }>`
  position: relative;
  width: 32px;
  height: 18px;
  border-radius: 999px;
  background: ${({ theme, $on }) =>
    $on ? theme.colors.primary : theme.colors.textLighter};
  transition: background-color 0.15s ease;
  flex-shrink: 0;
`

const SwitchThumb = styled.span<{ $on: boolean }>`
  position: absolute;
  top: 2px;
  left: ${({ $on }) => ($on ? '16px' : '2px')};
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.lightest};
  transition: left 0.15s ease;
`

const DisabledHint = styled(Text)`
  padding: 4px 16px 0;
`
